/**
 * DXF $INSUNITS validation.
 * Rejects files with INSUNITS 0 or missing (ambiguous mm/in).
 */

import { trackEvent, trackException } from '../telemetry';

const VALID_INSUNITS = [1, 2, 4, 5, 6]; // inches, feet, mm, cm, meters
// const HEADER_CHUNK_BYTES = 32768; // 32KB - $INSUNITS is always in HEADER at start
const HEADER_CHUNK_BYTES = 16384; // 16KB - $INSUNITS is always in HEADER at start
const BINARY_DXF_HEADER = "AutoCAD Binary DXF";


/**
 * Parse $INSUNITS from a DXF file header.
 * DXF format: group-code pairs. $INSUNITS uses group 9 (name) and 70 (integer value).
 * Only reads first 16KB to avoid loading large files; HEADER is always near the start.
 *
 * @param {File} file - DXF file
 * @returns {Promise<{ valid: boolean, insunits: number | null }>}
 */
async function parseDxfInsunits(file) {
  try {
    const chunk = file.slice(0, HEADER_CHUNK_BYTES);
    const buffer = await chunk.arrayBuffer();
    const text = new TextDecoder().decode(buffer);

    if (text.startsWith(BINARY_DXF_HEADER)) {
      trackEvent('dxf_binary_detected', { fileName: file.name });
      return { valid: false, insunits: null, isBinary: true };
    }

    const lines = text.split(/\r?\n/).map((l) => l.trim());

    for (let i = 0; i < lines.length - 1; i++) {
      if (lines[i].toUpperCase() === "$INSUNITS") {
        const nextCode = lines[i + 1];
        const nextValue = lines[i + 2];
        if (nextCode === "70" && nextValue !== undefined) {
          const insunits = parseInt(nextValue.trim(), 10);
          const valid = VALID_INSUNITS.includes(insunits);
          return { valid, insunits: Number.isNaN(insunits) ? null : insunits };
        }
      }
    }

    trackEvent('dxf_insunits_missing', { fileName: file.name });
    return { valid: false, insunits: null };
  } catch (err) {
    console.error(`[INSUNITS] ${file.name}: parse error |`, err);
    return { valid: false, insunits: null };
  }
}

/**
 * Get invalid DXF files, separated by failure reason.
 * Binary DXF files are returned separately — they cannot be fixed in the
 * frontend and are not supported by the backend parser (dxfgrabber is
 * ASCII-only). Users must re-save them as ASCII DXF.
 *
 * @param {File[]} files - DXF files to validate
 * @returns {Promise<{ invalidFiles: File[], binaryFiles: File[] }>}
 */
export async function getInvalidDxfFiles(files) {
  const invalidFiles = [];
  const binaryFiles = [];
  for (const file of files) {
    const result = await parseDxfInsunits(file);
    if (!result.valid) {
      if (result.isBinary) {
        binaryFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    }
  }
  return { invalidFiles, binaryFiles };
}

// dimensionType from UI: 0 = Millimeters (INSUNITS 4), 1 = Inches (INSUNITS 1)
const DIMENSION_TYPE_TO_INSUNITS = { 0: 4, 1: 1 };

/**
 * Fix a DXF file by adding or correcting $INSUNITS in the header.
 * Only supports ASCII DXF; binary DXF returns null.
 *
 * @param {File} file - DXF file
 * @param {number} dimensionType - 0 = Millimeters, 1 = Inches
 * @returns {Promise<File | null>} Fixed file or null if fix failed
 */
export async function fixDxfInsunits(file, dimensionType) {
  const insunits = DIMENSION_TYPE_TO_INSUNITS[dimensionType] ?? 1;
  try {
    const buffer = await file.arrayBuffer();
    const text = new TextDecoder().decode(buffer);

    // Binary DXF cannot be text-patched
    if (text.startsWith(BINARY_DXF_HEADER)) {
      trackEvent('dxf_binary_fix_skipped', { fileName: file.name });
      return null;
    }

    const lines = text.split(/\r?\n/);
    const eol = text.includes("\r\n") ? "\r\n" : "\n";

    let inHeader = false;
    let seenInsunits = false; // true once $INSUNITS is found (blocks spurious insertion at ENDSEC)
    let didFix = false;

    for (let i = 0; i < lines.length - 1; i++) {
      const code = lines[i].trim();
      const next = lines[i + 1]?.trim();

      if (code === "0" && next === "SECTION") {
        const sectionName = lines[i + 3]?.trim();
        if (sectionName === "HEADER") {
          inHeader = true;
        }
      }
      if (code === "0" && next === "ENDSEC") {
        if (inHeader && !seenInsunits) {
          // $INSUNITS missing — insert before ENDSEC
          const insert = ["  9", "$INSUNITS", " 70", `     ${insunits}`];
          lines.splice(i, 0, ...insert);
          didFix = true;
          break;
        }
        inHeader = false;
      }
      if (inHeader && next?.toUpperCase() === "$INSUNITS") {
        seenInsunits = true;
        const code70 = lines[i + 2]?.trim();
        const valueIdx = i + 3;
        if (code70 === "70" && valueIdx < lines.length) {
          lines[valueIdx] = String(insunits).padStart(6);
          didFix = true;
          break;
        }
      }
    }

    if (didFix) {
      const fixedText = lines.join(eol);
      return new File([fixedText], file.name, {
        type: file.type,
        lastModified: Date.now(),
      });
    }
    return null;
  } catch (err) {
    trackException(err instanceof Error ? err : new Error(String(err)), { fileName: file.name });
    return null;
  }
}
